import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Car, ShieldCheck, AlertCircle, AlertTriangle, Stethoscope, Send } from "lucide-react";
import { toast } from "sonner";
import { API, useRoleHome, useRoleAwareBackToJob } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";
import MOTAwarenessPanel from "@/components/intake/MOTAwarenessPanel";
import MOTOutcomeCaptureForm from "@/components/intake/MOTOutcomeCaptureForm";
import MOTOutcomeSummary from "@/components/intake/MOTOutcomeSummary";
import IntakeIntelTab from "@/components/intake/companion/IntakeIntelTab";
import MOTWorkflowControlCard from "@/components/intake/MOTWorkflowControlCard";
import MCCCardStack from "@/components/mot/MCCCardStack";
import MOTRapidCaptureCard from "@/components/mot/MOTRapidCaptureCard";
import TesterPrimaryActionCard from "@/components/mot/TesterPrimaryActionCard";
import KeyTagInline from "@/components/shared/KeyTagInline";

/**
 * MOT Workspace — Tester-side MOT day surface
 * --------------------------------------------------------------------------
 * Route: /app/tech/mot/:jobId
 *
 * Single canonical workspace for the MOT-day tester:
 *   1. Header strip — reg / vehicle / customer (links back to job)
 *   2. Pre-test Awareness Panel (MOT-1, Tester mode) — continuity layer
 *   3. Outcome capture form (MOT-2) when no outcome submitted yet
 *   4. Outcome summary + continuity hints when outcome exists
 *   5. Revision flow inline (reason required, original preserved server-side)
 *
 * Architectural rules respected:
 *   • Tester-owned. Severity is the tester's call.
 *   • Wording is preserved verbatim.
 *   • Outcome is immutable once submitted (revise only).
 *   • No upsell, no auto-pricing, no predictive language.
 */
export default function MOTWorkspacePage() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  // iter193 — role-aware back targets (shared helpers).
  // FOH/ADMIN → /app/foh/job/:id ; TECH → /app/tech/job/:id/sections
  // MOT_TESTER → /app/tech/mot (their own dashboard, iter196 fix).
  const backHref = useRoleAwareBackToJob(jobId);
  const homeHref = useRoleHome();
  const errorBackHref = homeHref;
  const isTester = homeHref === "/app/tech/mot";
  const isTech = homeHref === "/app/tech/jobs";
  const backLabel = isTester
    ? "Back to MOT queue"
    : (isTech ? "Back to vehicle inspection" : "Back to job");
  const errorBackLabel = isTester
    ? "Back to MOT queue"
    : (isTech ? "Back to jobs" : "Back to FOH dashboard");

  const [job, setJob] = useState(null);
  const [outcomePayload, setOutcomePayload] = useState(null);
  // VI-side mileage prefill — when the tester opens the workspace from a
  // job whose VI has already started, seed the odometer field on the
  // create form so they don't re-type. Silent-fail: prefill is enrichment.
  const [viMileage, setViMileage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviseOpen, setReviseOpen] = useState(false);
  // MOT Context Capture state — outcome_id + entries + schemas. Auto-loaded
  // alongside the outcome so the LEVEL 4 modal can pop on import.
  const [mccOutcomeId, setMccOutcomeId] = useState(null);
  const [mccEntries, setMccEntries] = useState([]);
  const [mccSchemas, setMccSchemas] = useState({});
  // Phase-1: Rapid Capture schemas + confidence/route option lists.
  // We render the new RapidCapture component for any entry that has a
  // rapid_capture block OR has no completed legacy data — falling back
  // to the legacy card stack only for already-completed legacy entries.
  const [rapidSchemas, setRapidSchemas] = useState({});
  const [rapidConfidenceOpts, setRapidConfidenceOpts] = useState([]);
  const [rapidRouteOpts, setRapidRouteOpts] = useState([]);
  // Phase 4 (Feb 2026) — Category picker grouping/frequency/search metadata.
  // Backend ships ``category_metadata`` (per-category group/rank/label/icon)
  // and ``category_groups`` (group ordering) alongside the schemas so the
  // FE picker can render the full ~40-category list grouped by vehicle
  // area, sorted by frequency, with a search box.
  const [rapidCategoryMetadata, setRapidCategoryMetadata] = useState({});
  const [rapidCategoryGroups, setRapidCategoryGroups] = useState([]);
  const [mccOpen, setMccOpen] = useState(false);
  const [mccBusy, setMccBusy] = useState(false);
  // iter197y — Auto-open suppression flag. Once the tester finishes or
  // explicitly closes the MCC modal in the current session, `loadMCC`
  // (which fires after every entry refresh / save) must NOT re-open
  // the modal automatically — otherwise the tester gets "looped back
  // to item one" when they click Save & Finish on the last card. The
  // flag is a ref (not state) so it never triggers a re-render; it is
  // reset whenever the jobId changes (i.e. the user navigates to a
  // different MOT workspace).
  const mccAutoOpenSuppressedRef = useRef(false);
  // Iter180 — bounce-back metadata for the "FOH asked you to complete MCC"
  // banner. Populated by loadMCC from the /jobs/{id}/mcc response.
  const [mccBounceBack, setMccBounceBack] = useState({ pending: false, log: [] });
  const [handoffError, setHandoffError] = useState("");
  const [handoffOk, setHandoffOk] = useState(false);
  // iter182 — "More options" advanced panel. The primary CTA card now
  // covers the 95% path (pull DVSA → complete MCC → submit). Manual
  // resolve / external import / manual capture become secondary, only
  // visible when the tester explicitly asks for them.
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Last action signal from the primary card — triggers a refetch so
  // outcome + MCC update without the parent having to know which API
  // was hit.
  const [primaryActionTick, setPrimaryActionTick] = useState(0);
  // iter194 — Toggled when the tester clicks "Capture today's MOT
  // result" on the historical-baseline banner. The capture form posts
  // through the new /mot-outcome/supersede-baseline endpoint so it
  // doesn't 409 against the historical outcome that's still on file.
  const [capturingTodayResult, setCapturingTodayResult] = useState(false);

  const loadJob = async () => {
    try {
      const r = await axios.get(`${API}/jobs/${jobId}`);
      setJob(r.data);
      return r.data;
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load job");
      return null;
    }
  };

  const loadInspectionMileage = async () => {
    try {
      const r = await axios.get(`${API}/jobs/${jobId}/inspection`);
      const m = r.data?.mileage;
      if (typeof m === "number" && m > 0) {
        setViMileage(m);
      }
    } catch {
      // Silent — prefill is enrichment, not a hard dependency.
    }
  };

  const loadOutcome = async () => {
    try {
      // Prefer fetching by job; falls through to linked session server-side.
      const r = await axios.get(`${API}/jobs/${jobId}/mot-outcome`);
      setOutcomePayload(r.data || null);
    } catch {
      setOutcomePayload(null);
    }
  };

  const loadMCC = async ({ allowAutoOpen = true } = {}) => {
    try {
      const r = await axios.get(`${API}/jobs/${jobId}/mcc`);
      const outcome_id = r.data?.outcome_id || null;
      let entries = r.data?.mcc_entries || [];
      // iter194b — when the backend reports the outcome is a
      // historical baseline import that hasn't been superseded,
      // MCC entries are intentionally suppressed. Do NOT trigger
      // the reclassify backfill below — that would reintroduce
      // MCC cards bound to last year's defects.
      const baselineSuppressed = r.data?.is_baseline_import === true;
      // Backfill on the fly for outcomes imported before MCC classification
      // shipped. One-shot, idempotent, server-side. Keeps the demo working
      // for existing data without a separate migration.
      if (
        !baselineSuppressed
        && outcome_id
        && entries.length === 0
        && (r.data?.defects || []).length > 0
      ) {
        try {
          const rc = await axios.post(`${API}/mcc/outcomes/${outcome_id}/reclassify`);
          entries = rc.data?.mcc_entries || [];
        } catch { /* non-fatal */ }
      }
      setMccOutcomeId(outcome_id);
      setMccEntries(entries);
      // Iter180 — bounce-back metadata. Shows the FOH's reason for
      // sending the job back so the tester knows what to focus on.
      setMccBounceBack({
        pending: !!r.data?.mcc_bounce_back_pending,
        log:     r.data?.mcc_bounce_back_log || [],
      });
      // If any LEVEL 4 entry is pending, auto-open the card stack — spec
      // says LEVEL 4 cards must surface as a blocking modal on import.
      const hasLevel4Pending = entries.some(
        (e) => (e.level || 0) >= 4 && e.status === "pending",
      );
      // iter197e — Only auto-open the modal for a FOH bounce-back when
      // there's at least one PENDING entry to act on. Without this
      // guard, an outcome whose bounce-back flag was never cleared
      // (stale data from before the iter197e backend fix) would keep
      // re-opening the modal on every workspace visit even though
      // every card is already complete — surfaced on SL59LYZ as
      // "stuck in MCC loop" after the tester finished all cards.
      const hasAnyPending = entries.some((e) => e.status === "pending");
      // iter197y — Two extra guards stacked on top of the iter197e fix:
      //   * `allowAutoOpen` lets callers (e.g. the entries-refresh
      //     hook fired from inside the modal) opt out of auto-opening.
      //   * `mccAutoOpenSuppressedRef` flips to true once the tester
      //     has actively closed or finished the modal in this session,
      //     stopping the "Save & Finish → loops back to item one"
      //     symptom that happened when the post-finish reload picked
      //     up a still-pending L4 entry and re-opened the modal at
      //     idx=0. Both guards are reset on jobId change.
      const canAutoOpen =
        allowAutoOpen && !mccAutoOpenSuppressedRef.current;
      if (
        canAutoOpen
        && (
          hasLevel4Pending
          || (r.data?.mcc_bounce_back_pending && hasAnyPending)
        )
      ) {
        setMccOpen(true);
      }
    } catch {
      setMccOutcomeId(null);
      setMccEntries([]);
    }
  };

  const loadMCCSchemas = async () => {
    try {
      const r = await axios.get(`${API}/mcc/schemas`);
      setMccSchemas(r.data?.schemas || {});
    } catch { /* non-fatal */ }
    // Phase-1: parallel fetch of rapid-capture schemas. Silent-fail
    // keeps the legacy stack working if the new endpoint is offline.
    try {
      const r2 = await axios.get(`${API}/mcc/rapid-capture/schemas`);
      setRapidSchemas(r2.data?.schemas || {});
      const schema_first = Object.values(r2.data?.schemas || {})[0] || {};
      setRapidConfidenceOpts(schema_first.pricing_confidence_options || []);
      setRapidRouteOpts(schema_first.repair_route_options || []);
      // Phase 4 — picker grouping payload. Silent-fail keeps the old
      // flat picker working if the BE hasn't been redeployed yet.
      setRapidCategoryMetadata(r2.data?.category_metadata || {});
      setRapidCategoryGroups(r2.data?.category_groups || []);
    } catch { /* non-fatal */ }
  };

  const load = async () => {
    setLoading(true);
    await loadJob();
    await loadOutcome();
    await loadInspectionMileage();
    await loadMCC();
    await loadMCCSchemas();
    setLoading(false);
  };

  useEffect(() => {
    // iter197y — Reset the auto-open suppression flag whenever the
    // tester navigates to a different MOT workspace. Within the same
    // workspace, once the modal has been closed/finished, we keep the
    // suppression so post-save reloads don't re-open the modal at
    // idx=0.
    mccAutoOpenSuppressedRef.current = false;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, primaryActionTick]);

  // ---- Submission handler ------------------------------------------------

  const handleSubmitted = async (data) => {
    setOutcomePayload(data);
    setReviseOpen(false);
  };

  /**
   * MOT-3 — decision change handler. The decision endpoint returns the
   * fresh decision_summary + outcome summary, but NOT the continuity hints
   * (those are derived from the immutable outcome body and don't change).
   * Easier to just re-fetch the full outcome envelope to keep all panels
   * coherent.
   */
  const handleDecisionChange = async () => {
    await loadOutcome();
  };

  // The capture form posts under the intake session id, since that's the
  // canonical anchor for an outcome. We resolve it from the job.
  const sessionId = job?.intake_session_id || null;

  // ---- Render ------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-4xl mx-auto p-8 text-center text-slate-500">
          <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
          Loading MOT workspace…
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-2xl mx-auto p-8">
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-700 mt-0.5" />
            <p className="text-[14px] text-red-900">{error || "Job not found."}</p>
          </div>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(errorBackHref)}
            data-testid="mot-workspace-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> {errorBackLabel}
          </Button>
        </div>
      </div>
    );
  }

  const outcome = outcomePayload?.outcome || null;
  const summary = outcomePayload?.summary || {};
  const continuityHints = outcomePayload?.continuity_hints || null;

  // iter194 — Detect the "DVSA returned last year's MOT" case. The
  // import path is the canonical signal; the summary mirror is for
  // resilience in case older outcome docs haven't been re-summarised.
  const isBaselineImport =
    outcome?.import_path === "external_mot_baseline"
    || summary?.is_baseline_import === true;
  const baselineAlreadySuperseded = !!(outcome?.superseded_at || summary?.superseded_at);
  const showBaselineBanner = isBaselineImport && !baselineAlreadySuperseded && !reviseOpen;

  // Capture form only renders when we have a linked intake session.
  // Without one, the tester has no canonical anchor to submit against —
  // surface a calm explanation rather than guessing.
  const canCapture = !!sessionId;

  const handleSupersedeSubmitted = async () => {
    setCapturingTodayResult(false);
    await load();
  };

  /**
   * Tyre size auto-transfer — when the tester types a tyre size on one
   * MCC card, broadcast it to other PENDING tyre MCC entries that have
   * no size yet. Mirrors the VI tyre "copy size to all tyres unless
   * staggered fit" UX. Empty-slot-only by design — never overwrites a
   * value the tester has already entered (which is how a staggered fit
   * is signalled). Local-only update; the value is persisted to the
   * server when the tester hits "Save & Next" on each sibling.
   */
  /**
   * iter191 — Tyre size/brand auto-propagation handler.
   *
   * When the tester enters the size OR brand on the first tyre MCC
   * entry, mirror it onto every OTHER pending tyre entry that hasn't
   * been set yet. Mirrors the VI flow's "copy tyre size/brand to all
   * tyres unless staggered fit" UX.
   *
   * Empty-slot-only by design — never overwrites a value the tester
   * has already entered on a sibling (that's how a staggered fit /
   * mixed brand is signalled).
   *
   * Fix (Feb 2026 — LJ65 WLC bug): now also persists the autofilled
   * field to the backend so the value sticks even if the tester
   * never manually opens that sibling entry. The propagation used
   * to be FE-state-only and got lost when the tester saved the
   * source entry and the modal reloaded.
   */
  const handlePropagateTyreSpec = (field, value, sourceEntryId) => {
    if (!field || !value || !sourceEntryId) return;
    if (field !== "size" && field !== "brand") return;
    const targets = [];
    setMccEntries((entries) =>
      entries.map((e) => {
        if (e.id === sourceEntryId) return e;
        if (e.status !== "pending") return e;
        const cat = (e.rapid_capture || {}).category;
        if (cat !== "tyre") return e;
        const existing = (e.rapid_capture?.fields || {})[field];
        if (existing) return e; // never overwrite a tester-set value
        targets.push(e.id);
        return {
          ...e,
          rapid_capture: {
            ...(e.rapid_capture || {}),
            fields: {
              ...((e.rapid_capture || {}).fields || {}),
              [field]: value,
            },
          },
          [`_autofilled_${field}`]: value,
        };
      })
    );
    // Persist to backend in parallel so the value survives modal
    // reloads. Non-blocking + best-effort — FE state has already
    // updated, so even if a write fails the tester sees the value
    // and can resave manually.
    if (mccOutcomeId && targets.length > 0) {
      targets.forEach((entryId) => {
        axios
          .put(
            `${API}/mcc/outcomes/${mccOutcomeId}/entries/${entryId}`,
            {
              rapid_capture: { fields: { [field]: value } },
              auto_complete: false,
            },
          )
          .catch(() => { /* non-fatal; FE state already set */ });
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-4xl mx-auto px-5 sm:px-6 py-6">
        <button
          onClick={() => navigate(backHref)}
          className="text-slate-500 hover:text-slate-800 inline-flex items-center text-[16px] mb-4 font-medium"
          data-testid="mot-workspace-back-to-job"
        >
          <ArrowLeft className="w-5 h-5 mr-1.5" /> {backLabel}
        </button>

        {/* Header strip */}
        <div
          className="rounded-xl border border-slate-200 bg-white px-6 py-5 mb-5 flex flex-wrap items-center justify-between gap-4"
          data-testid="mot-workspace-header"
        >
          <div className="flex items-center gap-4 min-w-0">
            <div
              className="inline-flex items-center justify-center bg-yellow-300 border-2 border-slate-900 px-4 py-2 rounded-md font-mono font-bold tracking-wider text-slate-900 text-[22px] shadow-sm"
              data-testid="mot-workspace-reg-plate"
            >
              <Car className="w-5 h-5 mr-2 opacity-60" />
              {(job.reg || "—").toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-[15px] uppercase tracking-[0.18em] text-slate-600 font-semibold flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-teal-600" />
                MOT workspace
              </p>
              <h1 className="text-[28px] font-bold text-slate-900 leading-tight">
                {job.customer_name || job.customer || "—"}
              </h1>
              <p className="text-[17px] text-slate-600 mt-0.5">
                {[job.make, job.model, job.year].filter(Boolean).join(" ")}
              </p>
            </div>
          </div>
          {/* Key tag — physical key reference. Critical for matching
              the on-hand key to the right car in a busy workshop. */}
          <KeyTagInline
            jobId={job.id}
            initial={job.key_tag_number}
            size="lg"
            onSaved={(v) => setJob((j) => (j ? { ...j, key_tag_number: v } : j))}
          />
        </div>

        {/* Pre-test awareness — only when there's an intake session anchor. */}
        {sessionId && (
          <MOTAwarenessPanel
            sessionId={sessionId}
            mode="tester"
            className="mb-5"
          />
        )}

        {/* Intake Intel — captured FOH intel + customer recap (Phase 2).
            Tester mode strips Service + Diagnostic noise and hides the
            customer-recap card (FOH-only artefact). */}
        {sessionId && (
          <IntakeIntelTab
            intakeSessionId={sessionId}
            mode="tester"
            className="mb-5"
          />
        )}

        {/* iter182 — Unified primary action card. Replaces the previous
            three competing buttons (Check DVSA / Import External /
            Resolve Manually). State-aware: shows exactly ONE primary CTA
            depending on outcome + MCC progress.

            iter194b — Suppressed entirely while the outcome on file is
            a historical baseline import (DVSA fell back to last year's
            MOT). Otherwise the card would either show "Complete MCC (n
            cards)" against last year's defects OR — once MCC entries
            are suppressed — show "MCC complete — Submit to FOH",
            which would push last year's defects to FOH for pricing.
            The amber banner below already provides the correct CTA:
            "Capture today's MOT result → start MCC". */}
        {sessionId && !showBaselineBanner && (
          <div className="mb-5">
            <TesterPrimaryActionCard
              sessionId={sessionId}
              jobId={jobId}
              outcomeId={mccOutcomeId || outcome?.id}
              mccEntries={mccEntries}
              submittedAt={outcome?.mcc_released_at}
              onAction={(event) => {
                // Bump the re-fetch ticker for any state change.
                setPrimaryActionTick((n) => n + 1);
                // Once the MCC has been released to FOH, the tester's work
                // on this job is done — drop them straight back on the
                // Today's MOTs queue with a confirmation toast so they
                // don't have to manually navigate back.
                if (event === "submitted") {
                  const regLabel = (job?.vehicle_reg || job?.reg || "MOT").toUpperCase();
                  toast.success(
                    `${regLabel} submitted to FOH — back on the queue.`,
                    { duration: 4500 },
                  );
                  // Tiny delay lets the success banner inside the action
                  // card flash before we navigate away — feels less abrupt.
                  setTimeout(() => navigate("/app/tech/mot"), 650);
                }
              }}
              onOpenMcc={() => setMccOpen(true)}
              onOpenAdvanced={() => setShowAdvanced((v) => !v)}
            />
          </div>
        )}

        {/* "More options" — legacy/edge-case surfaces. Manual resolve,
            external MOT import, schedule slot, and the manual outcome
            capture form. Hidden by default to keep the primary path
            uncluttered; the tester opens it via the kebab on the
            primary card.                                                */}
        {sessionId && showAdvanced && (
          <details
            open
            className="mb-5 rounded-lg border border-slate-200 bg-white"
            data-testid="mot-workspace-advanced-panel"
          >
            <summary className="px-5 py-3 text-[13px] font-semibold text-slate-700 cursor-pointer flex items-center justify-between">
              <span>More options — manual resolve, scheduling, manual capture</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowAdvanced(false);
                }}
                className="text-[11px] uppercase tracking-wide text-slate-500 hover:text-slate-800"
                data-testid="mot-workspace-advanced-close"
              >
                Hide
              </button>
            </summary>
            <div className="px-5 pb-5 pt-1 space-y-4">
              {!outcome && (
                <MOTWorkflowControlCard
                  sessionId={sessionId}
                  onImported={() => load()}
                />
              )}
              {!outcome && canCapture && (
                <div>
                  <p className="text-[12px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
                    Or capture the outcome manually (no DVSA fetch)
                  </p>
                  <MOTOutcomeCaptureForm
                    sessionId={sessionId}
                    jobId={jobId}
                    mode="create"
                    onSubmitted={handleSubmitted}
                    prefillOdometer={viMileage}
                    prefillOdometerUnit="mi"
                  />
                </div>
              )}
            </div>
          </details>
        )}

        {/* iter194 — Historical baseline banner. The DVSA pull
            returned LAST YEAR'S MOT because today's hasn't been
            submitted yet. Make that crystal clear and give the
            tester a one-tap path to capture today's result + start
            MCC. Without this, the workspace looked identical to a
            "today's MOT already submitted" state. */}
        {showBaselineBanner && !capturingTodayResult && outcome && (
          <section
            className="mb-5 rounded-xl border-2 border-amber-400 bg-amber-50 px-5 py-4"
            data-testid="mot-historical-baseline-banner"
            data-outcome-id={outcome.id}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-amber-700 mb-0.5">
                  Historical context — last year's MOT
                </p>
                <h2 className="text-[18px] font-bold text-amber-900 leading-tight">
                  Last MOT: {outcome.test_result || summary.test_result || "—"}
                  {(outcome.test_completed_at || summary.test_completed_at) && (
                    <span className="font-normal text-amber-800">
                      {" "}
                      · {new Date(outcome.test_completed_at || summary.test_completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  )}
                </h2>
                <p className="text-[13px] text-amber-900 mt-1.5 leading-snug">
                  {(() => {
                    const defects = outcome.defects || [];
                    const counts = summary.counts || {};
                    const dangerous = counts.dangerous || 0;
                    const major = counts.major || 0;
                    const minor = counts.minor || 0;
                    const advisory = counts.advisory || 0;
                    const parts = [];
                    if (dangerous) parts.push(`${dangerous} dangerous`);
                    if (major) parts.push(`${major} major`);
                    if (minor) parts.push(`${minor} minor`);
                    if (advisory) parts.push(`${advisory} advisory`);
                    const summaryText = parts.length
                      ? `${defects.length} defect${defects.length !== 1 ? "s" : ""} (${parts.join(", ")}).`
                      : `No defects recorded.`;
                    const expiry = outcome.mot_expiry_date || summary.mot_expiry_date;
                    const expiryText = expiry
                      ? ` Current MOT expires ${new Date(expiry).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}.`
                      : "";
                    return summaryText + expiryText;
                  })()}
                </p>
                {(outcome.defects || []).length > 0 && (
                  <details className="mt-2.5 text-[12.5px] text-amber-900">
                    <summary className="cursor-pointer font-semibold hover:underline">
                      View last year's defect list ({(outcome.defects || []).length})
                    </summary>
                    <ul className="mt-2 space-y-1 list-disc list-inside marker:text-amber-700">
                      {(outcome.defects || []).slice(0, 12).map((d, i) => (
                        <li key={i} className="leading-snug">
                          <span className="font-medium uppercase text-[10.5px] tracking-wider mr-1">
                            {(d.severity || d.category || "").toUpperCase()}
                          </span>
                          {d.text || d.description || d.title || "Defect"}
                        </li>
                      ))}
                      {(outcome.defects || []).length > 12 && (
                        <li className="text-amber-700 italic">… and {(outcome.defects || []).length - 12} more</li>
                      )}
                    </ul>
                  </details>
                )}
                <div className="mt-4 flex items-center gap-2">
                  <Button
                    onClick={() => setCapturingTodayResult(true)}
                    className="bg-amber-700 hover:bg-amber-800 text-white"
                    data-testid="capture-todays-mot-btn"
                  >
                    Capture today's MOT result → start MCC
                  </Button>
                  <p className="text-[11.5px] text-amber-800">
                    This will replace the historical record with the test you've just completed.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* iter194 — Capture form in "supersede" mode. Same UI as
            create, but POSTs to the supersede-baseline endpoint so
            the historical outcome is preserved + flagged superseded. */}
        {showBaselineBanner && capturingTodayResult && (
          <MOTOutcomeCaptureForm
            sessionId={sessionId}
            jobId={jobId}
            mode="create"
            supersedeBaselineOutcomeId={outcome?.id}
            onSubmitted={handleSupersedeSubmitted}
            onCancel={() => setCapturingTodayResult(false)}
            prefillOdometer={viMileage}
            prefillOdometerUnit="mi"
          />
        )}

        {/* Outcome — either summary (already submitted) or capture form. */}
        {outcome && !reviseOpen && !showBaselineBanner && (
          <MOTOutcomeSummary
            outcome={outcome}
            summary={summary}
            continuityHints={continuityHints}
            decisionSummary={outcomePayload?.decision_summary}
            sessionId={sessionId}
            onRevise={() => setReviseOpen(true)}
            onDecisionChange={handleDecisionChange}
            testerView
          />
        )}

        {outcome && reviseOpen && (
          <MOTOutcomeCaptureForm
            sessionId={sessionId}
            jobId={jobId}
            mode="revise"
            initial={outcome}
            onSubmitted={handleSubmitted}
            onCancel={() => setReviseOpen(false)}
          />
        )}

        {!outcome && !canCapture && (
          <section
            className="rounded-xl border border-amber-200 bg-amber-50/60 px-6 py-5"
            data-testid="mot-workspace-no-session"
          >
            <p className="text-[12px] uppercase tracking-[0.18em] text-amber-700 font-semibold">
              No intake session linked
            </p>
            <p className="text-[14.5px] text-amber-900 leading-snug mt-1">
              This job isn't linked to a Yestimate intake session. The MOT
              outcome surface anchors to the intake session. Link the job to
              an intake or have the FOH create one before recording the
              outcome here.
            </p>
          </section>
        )}

        {/* ===== MOT Context Capture (MCC) ===================================
            Bridge between the DVSA outcome and the FOH pricing/portal.
            Tester captures structured evidence per defect, then releases
            the job to FOH via "Ready For FOH". LEVEL 4 cards block the
            handoff; LEVEL 2/3 never do. */}
        {mccBounceBack.pending && (() => {
          const latest = (mccBounceBack.log || []).slice(-1)[0] || {};
          return (
            <section
              className="mt-6 rounded-xl border-2 border-purple-400 bg-purple-50 px-5 py-4 flex items-start gap-3"
              data-testid="mcc-bounce-back-banner"
            >
              <div className="rounded-full bg-purple-100 p-2 shrink-0">
                <Send className="w-5 h-5 text-purple-700" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-purple-900">
                  FOH asked you to complete MCC
                </p>
                {latest.reason && (
                  <p className="text-sm text-purple-800 mt-1 leading-snug italic">
                    "{latest.reason}"
                  </p>
                )}
                <p className="text-xs text-purple-700/80 mt-1">
                  {latest.by_name && <>— {latest.by_name} · </>}
                  {latest.added_entries > 0
                    ? `${latest.added_entries} new card(s) added`
                    : `${latest.pending_after_bounce || 0} card(s) pending`}
                </p>
              </div>
            </section>
          );
        })()}

        {mccOutcomeId && mccEntries.length > 0 && (
          <section className="mt-6 space-y-3" data-testid="mcc-modal-portal">
            {/* iter182 — banner + Ready-for-FOH duplicate removed. The
                unified TesterPrimaryActionCard above handles the
                "Complete MCC" and "Submit to FOH" CTAs in a single
                state-aware surface. We keep the modal portal here so
                opening the rapid-capture flow continues to work. */}
            {mccOpen && (
              <div
                className="fixed inset-0 bg-slate-900/70 z-50 flex items-start justify-center p-4 overflow-y-auto"
                data-testid="mcc-modal"
              >
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-4">
                  {/* Phase-1: Rapid Capture is the default. Legacy stack
                      only renders when the new schemas haven't loaded
                      yet (network/feature-flag fallback) so already-
                      completed legacy entries remain readable. */}
                  {Object.keys(rapidSchemas).length > 0 ? (
                    <MOTRapidCaptureCard
                      outcomeId={mccOutcomeId}
                      entries={mccEntries}
                      schemas={rapidSchemas}
                      pricingConfidenceOptions={rapidConfidenceOpts}
                      repairRouteOptions={rapidRouteOpts}
                      categoryMetadata={rapidCategoryMetadata}
                      categoryGroups={rapidCategoryGroups}
                      onPropagateTyreSpec={handlePropagateTyreSpec}
                      onClose={async () => {
                        // iter197y — User has explicitly closed the
                        // modal; suppress auto-reopen for the rest of
                        // this workspace session.
                        mccAutoOpenSuppressedRef.current = true;
                        setMccOpen(false);
                        await loadMCC({ allowAutoOpen: false });
                      }}
                      onCompleteAll={async () => {
                        // iter197y — Last entry Save & Finish landed.
                        // Don't let the post-completion refresh push
                        // the tester back to item one if a stale L4
                        // flag is still in the response.
                        mccAutoOpenSuppressedRef.current = true;
                        setMccOpen(false);
                        await loadMCC({ allowAutoOpen: false });
                      }}
                      onEntriesRefresh={async () => {
                        // iter197y — In-modal refresh (category pick /
                        // re-open). Pulls the latest entries WITHOUT
                        // closing the modal or re-triggering auto-open.
                        await loadMCC({ allowAutoOpen: false });
                      }}
                    />
                  ) : (
                    <MCCCardStack
                      outcomeId={mccOutcomeId}
                      entries={mccEntries}
                      schemas={mccSchemas}
                      onClose={async () => {
                        mccAutoOpenSuppressedRef.current = true;
                        setMccOpen(false);
                        await loadMCC({ allowAutoOpen: false });
                      }}
                      onCompleteAll={async () => {
                        mccAutoOpenSuppressedRef.current = true;
                        setMccOpen(false);
                        await loadMCC({ allowAutoOpen: false });
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
